import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { summarizationError } from "../lib/summarizationError";

const API = '/api';

function SectionHeader({ num, title }) {
  return (
    <div className="sec-divider">
      <span className="sec-num">{num}</span>
      <div className="sec-line" />
      <span className="sec-title">{title}</span>
      <div className="sec-mark" />
    </div>
  );
}

export default function SummarizePage() {
  const [url, setUrl] = useState("");
  const [saveToNotion, setSaveToNotion] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("");
  const [summary, setSummary] = useState(null);
  const [notionSaving, setNotionSaving] = useState(false);
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState("");
  const [hasNotion, setHasNotion] = useState(false);
  const [notionDbs, setNotionDbs] = useState([]);
  const [selectedDb, setSelectedDb] = useState("");

  useEffect(() => {
    Promise.all([axios.get(`${API}/channels`), axios.get(`${API}/settings`)])
      .then(([c, s]) => { setChannels(c.data); setHasNotion(s.data.has_notion || false); setNotionDbs(s.data.notion_databases || []); })
      .catch(() => {});
  }, []);

  const handleSummarize = async () => {
    if (!url.trim()) { toast.error("Enter a YouTube URL"); return; }
    setLoading(true); setSummary(null); setStep("RETRIEVING TRANSCRIPT AND GENERATING SUMMARY...");
    try {
      const res = await axios.post(`${API}/summarize`, {
        video_url: url.trim(),
        channel_id: selectedChannel || null,
        save_to_notion: saveToNotion && hasNotion,
        notion_database_id: selectedDb || null,
      }, { timeout: 180000 });
      setSummary(res.data);
      toast.success("DOCUMENT PROCESSED");
    } catch (e) {
      toast.error(summarizationError(e));
    } finally {
      setLoading(false); setStep("");
    }
  };

  const handleSaveToNotion = async () => {
    if (!hasNotion) { toast.error("Configure Notion in Settings"); return; }
    setNotionSaving(true);
    try {
      const res = await axios.post(`${API}/summaries/${summary.id}/save-to-notion`);
      setSummary(prev => ({ ...prev, notion_page_id: res.data.notion_url, notion_url: res.data.notion_url }));
      toast.success("SAVED TO NOTION");
    } catch (e) {
      toast.error(e.response?.data?.detail || "NOTION SAVE FAILED");
    } finally {
      setNotionSaving(false); }
  };

  return (
    <div className="anim-in r-full" style={{ maxWidth: 820 }} data-testid="summarize-page">
      {/* Page header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", letterSpacing: "0.2em", color: "#CC0000", marginBottom: 6 }}>
          PROCESS / NEW DOCUMENT
        </div>
        <h1 style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "2rem", fontWeight: 700, color: "#111", margin: 0, lineHeight: 1 }}>
          SUMMARIZE
        </h1>
        <div style={{ height: 3, background: "#111", marginTop: 10, width: 64 }} />
      </div>

      {/* Input section */}
      <div style={{ marginBottom: 24 }}>
        <SectionHeader num="01" title="INPUT / VIDEO URL" />

        <div style={{ background: "#fff", border: "2px solid #111", padding: "24px", position: "relative" }}>
          <div style={{ position: "absolute", top: -3, left: -3, width: 6, height: 6, background: "#CC0000" }} />
          <div style={{ position: "absolute", top: -3, right: -3, width: 6, height: 6, background: "#CC0000" }} />

          <label style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "0.58rem",
            letterSpacing: "0.16em",
            color: "#888",
            textTransform: "uppercase",
            display: "block",
            marginBottom: 8,
          }}>
            YOUTUBE VIDEO URL
          </label>
          <input
            data-testid="youtube-url-input"
            type="url"
            value={url}
            onChange={e => setUrl(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSummarize()}
            placeholder="https://www.youtube.com/watch?v=..."
            className="ind-input"
            style={{ marginBottom: 16 }}
          />

          {/* Options row */}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", marginBottom: 16, paddingTop: 12, borderTop: "1px solid #e8e8e4", rowGap: 10 }}>
            {channels.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", letterSpacing: "0.14em", color: "#888" }}>CHANNEL:</span>
                <select
                  data-testid="channel-select"
                  value={selectedChannel}
                  onChange={e => setSelectedChannel(e.target.value)}
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "0.65rem",
                    background: "#fff",
                    border: "1px solid #111",
                    padding: "4px 8px",
                    color: "#111",
                    outline: "none",
                  }}
                >
                  <option value="">NONE</option>
                  {channels.map(ch => <option key={ch.id} value={ch.id}>{ch.name.toUpperCase()}</option>)}
                </select>
              </div>
            )}
            {notionDbs.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", letterSpacing: "0.14em", color: "#888" }}>DATABASE:</span>
                  <select
                    data-testid="notion-db-select"
                    value={selectedDb}
                    onChange={e => setSelectedDb(e.target.value)}
                    style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.65rem", background: "#fff", border: "1px solid #111", padding: "4px 8px", color: "#111", outline: "none" }}
                  >
                    <option value="">AUTO (BY CATEGORY)</option>
                    {notionDbs.map(d => <option key={d.database_id} value={d.database_id}>{(d.category || "").toUpperCase()}</option>)}
                  </select>
                </div>
              )}
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }} data-testid="save-to-notion-toggle">
              <div
                onClick={() => {
                  if (!hasNotion) { toast.info("Configure Notion in Settings"); return; }
                  setSaveToNotion(!saveToNotion);
                }}
                style={{
                  width: 32, height: 16,
                  background: saveToNotion && hasNotion ? "#111" : "#ddd",
                  position: "relative",
                  cursor: "pointer",
                  transition: "background 0.12s",
                  flexShrink: 0,
                }}
              >
                <div style={{
                  position: "absolute",
                  top: 2, left: saveToNotion && hasNotion ? 18 : 2,
                  width: 12, height: 12,
                  background: saveToNotion && hasNotion ? "#CC0000" : "#fff",
                  border: "1px solid rgba(0,0,0,0.2)",
                  transition: "left 0.12s, background 0.12s",
                }} />
              </div>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", letterSpacing: "0.14em", color: "#888" }}>
                SAVE TO NOTION {!hasNotion && <span style={{ color: "#ccc" }}>(SETUP REQ.)</span>}
              </span>
            </label>
          </div>

          <button
            data-testid="summarize-btn"
            onClick={handleSummarize}
            disabled={loading}
            className="ind-btn-primary"
            style={{ width: "100%", justifyContent: "center", padding: "14px" }}
          >
            {loading ? (
              <><Loader2 size={14} className="animate-spin" /> {step}</>
            ) : (
              "PROCESS WITH AI →"
            )}
          </button>
        </div>
      </div>

      {/* Result */}
      {summary && (
        <div className="anim-in">
          <SectionHeader num="02" title="PROCESSED DOCUMENT" />
          <SummaryResult summary={summary} onSaveToNotion={handleSaveToNotion} notionSaving={notionSaving} hasNotion={hasNotion} />
        </div>
      )}
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: "0.55rem",
        letterSpacing: "0.2em",
        color: "#CC0000",
        textTransform: "uppercase",
        marginBottom: 6,
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}>
        <div style={{ width: 4, height: 4, background: "#CC0000" }} />
        {label}
      </div>
      <div style={{ borderLeft: "2px solid #e8e8e4", paddingLeft: 16 }} data-testid={testId}>
        {children}
      </div>
    </div>
  );
}

function SummaryResult({ summary, onSaveToNotion, notionSaving, hasNotion }) {
  return (
    <div style={{ border: "2px solid #111", background: "#fff", position: "relative" }} data-testid="summary-result">
      <div style={{ position: "absolute", top: -3, left: -3, width: 6, height: 6, background: "#CC0000" }} />
      <div style={{ position: "absolute", top: -3, right: -3, width: 6, height: 6, background: "#CC0000" }} />
      <div style={{ position: "absolute", bottom: -3, left: -3, width: 6, height: 6, background: "#111" }} />
      <div style={{ position: "absolute", bottom: -3, right: -3, width: 6, height: 6, background: "#111" }} />

      {/* Video header */}
      <div style={{ borderBottom: "2px solid #111", padding: "16px 20px", display: "flex", gap: 16, alignItems: "center", background: "#111" }}>
        {summary.thumbnail_url && (
          <img src={summary.thumbnail_url} alt="" style={{ width: 80, height: 50, objectFit: "cover", flexShrink: 0, opacity: 0.85 }} onError={e => { e.target.style.display = "none"; }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <a href={summary.video_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.75rem", fontWeight: 700, color: "#fff", letterSpacing: "0.06em", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} data-testid="summary-video-title">
              {summary.video_title.toUpperCase()}
            </div>
          </a>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.58rem", color: "#888", letterSpacing: "0.1em" }}>
            {summary.channel_name.toUpperCase()} · YOUTUBE
          </div>
        </div>
        {summary.notion_page_id && (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.55rem", background: "#CC0000", color: "#fff", padding: "3px 8px", letterSpacing: "0.1em", flexShrink: 0 }}>
            ✓ NOTION
          </span>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: "24px" }}>
        <Field label="SUBJECT" testId="summary-subject">
          <p style={{ fontFamily: "Inter, sans-serif", fontSize: "0.875rem", color: "#333", lineHeight: 1.6, margin: 0, fontStyle: "italic" }}>
            {summary.subject}
          </p>
        </Field>

        <Field label="SUMMARY" testId="summary-text">
          <p style={{ fontFamily: "Inter, sans-serif", fontSize: "0.875rem", color: "#333", lineHeight: 1.7, margin: 0 }}>
            {summary.summary}
          </p>
        </Field>

        <Field label="KEY POINTS" testId="summary-key-points">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {summary.key_points.map((pt, i) => (
              <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "0.6rem", color: "#CC0000", fontWeight: 700, flexShrink: 0, marginTop: 2 }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span style={{ fontFamily: "Inter, sans-serif", fontSize: "0.875rem", color: "#333", lineHeight: 1.5 }}>{pt}</span>
              </div>
            ))}
          </div>
        </Field>

        <Field label="CONCLUSION" testId="summary-conclusion">
          <p style={{ fontFamily: "Inter, sans-serif", fontSize: "0.875rem", color: "#333", lineHeight: 1.7, margin: 0 }}>
            {summary.conclusion}
          </p>
        </Field>
      </div>

      {/* Footer actions */}
      <div style={{ borderTop: "1px solid #e8e8e4", padding: "16px 20px", display: "flex", gap: 8, background: "#FAFAF8" }}>
        <Link to={`/summary/${summary.id}`} style={{ textDecoration: "none" }}>
          <button className="ind-btn-ghost" data-testid="view-full-summary-btn">VIEW FULL →</button>
        </Link>
        {!summary.notion_page_id && (
          <button
            className="ind-btn-ghost"
            data-testid="save-to-notion-btn"
            onClick={onSaveToNotion}
            disabled={notionSaving || !hasNotion}
            title={!hasNotion ? "Configure Notion in Settings" : ""}
          >
            {notionSaving ? <Loader2 size={12} className="animate-spin" /> : null}
            SAVE TO NOTION
          </button>
        )}
        {summary.notion_url && (
          <a href={summary.notion_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
            <button className="ind-btn-primary" data-testid="open-notion-link" style={{ background: "#111", borderColor: "#111" }}>
              ↗ OPEN IN NOTION
            </button>
          </a>
        )}
      </div>
    </div>
  );
}
